import React, { useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import withStyles from 'react-jss';
import PropTypes from 'prop-types';
import { Table, Popconfirm, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { EditOutlined, DeleteOutlined, UndoOutlined } from '@ant-design/icons';

import Breadcrumbs from '../Components/Breadcrumbs';
import { sorter, onFilter, filterDropdown, filterIcon, get } from '../utils';
import Api from '../Api';
import { getUsers, clearUsers } from '../Actions';
import { errorNotification, successNotification } from '../Snackbar';
import styles from '../styles';
import constants from '../constants';

const UserList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const users = useSelector(state => state.users);

  useEffect(() => {
    dispatch(getUsers());
    return () => {
      dispatch(clearUsers());
    };
  }, []);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/users/${id}`);
      successNotification('User deleted');
      dispatch(getUsers());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const onUnDelete = useCallback(async id => {
    try {
      await Api.put(`/users/restore/${id}`);
      successNotification('User recovered');
      dispatch(getUsers());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.updateUser) &&
          record.isDeleted === false ? (
            <Tooltip placement="bottom" title="Update">
              <Link to={`/users/update/${record.id}`}>
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const deleteColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.deleteUser) &&
          record.isDeleted === false ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const recoverColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.recoverUser) &&
          record.isDeleted === true ? (
            <Tooltip placement="bottom" title="Recover">
              <Popconfirm
                title="Are you sure?"
                onConfirm={() => onUnDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <UndoOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: sorter('name'),
        onFilter: onFilter('name'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        sorter: sorter('email'),
        onFilter: onFilter('email'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        sorter: sorter('phone'),
        onFilter: onFilter('phone'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Address',
        dataIndex: 'address',
        sorter: sorter('address'),
        onFilter: onFilter('address'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'comment',
        sorter: sorter('comment'),
        onFilter: onFilter('comment'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Role',
        dataIndex: 'role',
        sorter: sorter('role'),
        onFilter: onFilter('role'),
        filterDropdown,
        filterIcon,
      },
    ];

    if (permissions.includes(constants.permissions.updateUser)) {
      column.push({
        title: 'Update',
        render: updateColumn,
      });
    }

    if (permissions.includes(constants.permissions.deleteUser)) {
      column.push({
        title: 'Delete',
        render: deleteColumn,
      });
    }

    if (permissions.includes(constants.permissions.recoverUser)) {
      column.push({
        title: 'Recover',
        render: recoverColumn,
      });
    }
    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1200, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="User"
        moduleLink="/users/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={users}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

UserList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(UserList);
